import { useEffect } from "react";
import { Box, CircularProgress, Container, GridLegacy as Grid } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import FeatureTile from "../components/tiles/FeatureTile";
import ProductTile from "../components/tiles/ProductTile";
import {
    Accessible,
    AssignmentInd,
    BallotOutlined,
    BeachAccess,
    CloudDone,
    EmojiObjects,
    ImportantDevices,
    Info,
    InfoOutlined,
    LocalHospital,
    MenuBook,
    Warning,
    Description,
    School,
    SportsSoccer,
    HelpOutline,
} from "@mui/icons-material";
import layoutConstants from "../constants/layoutConstants";
import SpannableTile from "../components/tiles/SpannableTile";
import referralImage from "../images/referral_banner.png";
import { useTracking } from "react-tracking";
import { useDispatch, useSelector } from "react-redux";
import useAccountFeatures from "../hooks/queries/useAccountFeatures";
import { setActiveAppId } from "../redux/actions/accountActions";
import ErrorBox from "../components/ErrorBox";
import PageBase from "../components/PageBase";
import { isHRS } from "../constants/siteConfig";

// Icons referenced by the EWClient tile table (`tile.icon`), imported by name so
// only these ship. Previously a `import * as muiIcons` namespace import pulled in
// all ~2,100 MUI icons (~3.7MB). Unknown/mistyped names fall back to HelpOutline.
const tileIcons: Record<string, typeof HelpOutline> = {
    Accessible,
    AssignmentInd,
    BallotOutlined,
    BeachAccess,
    CloudDone,
    EmojiObjects,
    ImportantDevices,
    Info,
    InfoOutlined,
    LocalHospital,
    MenuBook,
    Warning,
    Description,
    School,
    SportsSoccer,
};

const useStyles = makeStyles((theme) => ({
    tilesGrid: {
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${layoutConstants.featureTileWidth}px, 1fr))`,
        gap: theme.spacing(layoutConstants.tilesGridSpacingMultiplier),
    },
    loadingContainer: {
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
    },
}));

const appEnvironment = process.env.REACT_APP_ENV;
const isProduction = appEnvironment === "production";
const isDevelopment = appEnvironment === "development";
const tileRouteTokenReplacement = isProduction ? "" : isDevelopment ? "test" : appEnvironment;

const Home = () => {
    const dispatch = useDispatch();
    const { Track } = useTracking({
        page: "Home",
    });
    const { selectedAccount } = useSelector((state: any) => state.account);
    const classes = useStyles();
    const { data, isLoading, isError, error } = useAccountFeatures(selectedAccount?.childExternalIds);

    useEffect(() => {
        dispatch(setActiveAppId(null));
    }, [dispatch]);

    const goToReferralPage = () => {
        window.open("https://worknest.com/referral/?v=myworknest", "_blank");
    };

    const featureTiles = () => {
        if (isLoading)
            return (
                <Box className={classes.loadingContainer}>
                    <CircularProgress color="primary" />
                </Box>
            );

        if (isError) return <ErrorBox message={error.message} />;

        return (
            <Track>
                <ProductTile />
                <div className={classes.tilesGrid}>
                    
                    {data &&
                        data.map((tile, index) => (
                            <FeatureTile
                                key={index}
                                title={tile.displayName}
                                icon={tileIcons[tile.icon] ?? HelpOutline}
                                image={tile.imageUrl}
                                imageText={tile.imageText}
                                internalRoute={tile.internalRoute}
                                externalRoute={tile.clickUrl?.replace("{{ENV}}", tileRouteTokenReplacement)}
                            />
                        ))}           
                    {!isHRS &&
                        <SpannableTile
                            spanCols={3}
                            bgImageUrl={referralImage}
                            onClick={goToReferralPage}
                        />
                    }
                </div>
            </Track>
        );
    };

    return (
        <PageBase>
            <Container maxWidth="lg">
                <Grid container spacing={layoutConstants.homePageSectionsPaddingMultiplier}>
                    <Grid item xs={12}>
                        {featureTiles()}
                    </Grid>
                </Grid>
            </Container>
        </PageBase>
    );
};

export default Home;
